import React from 'react';
import { useSessionState } from '../shared/hooks/session-state-hook';
import './VitalsChart.css';

export const VITAL_METRICS = {
  blood_pressure: { label: 'Blood pressure', unit: 'mmHg' },
  glucose: { label: 'Glucose', unit: 'mg/dL' },
  weight: { label: 'Weight', unit: 'kg' },
  pulse: { label: 'Pulse', unit: 'bpm' },
  temperature: { label: 'Temperature', unit: '°C' },
  spo2: { label: 'Oxygen saturation', unit: '%' }
};

const CONTEXTS = { fasting: 'Fasting', 'after-meal': 'After meal', random: 'Random' };
const DAY = 86400000;
const owns = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const contextLabel = (context) => (owns(CONTEXTS, context) ? CONTEXTS[context] : context);
const WIDTH = 640;
const HEIGHT = 260;
const PLOT = { left: 58, right: 612, top: 24, bottom: 206 };
const roles = { patient: 'Patient', doctor: 'Doctor', pharmacy: 'Pharmacist' };
const source = (entry) =>
  [entry.author?.name || 'Unknown author', roles[entry.author?.role] || entry.author?.role]
    .filter(Boolean)
    .join(' · ');
const dateLabel = (timestamp) =>
  new Date(timestamp).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
const dateTimeLabel = (timestamp) =>
  new Date(timestamp).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short'
  });
const finitePositive = (value) => typeof value === 'number' && Number.isFinite(value) && value > 0;

// Reject malformed/undated records instead of treating missing values as zero.
export const normalizeVitalEntries = (entries, now = Date.now()) =>
  (Array.isArray(entries) ? entries : [])
    .filter((entry) => {
      const measurement = entry?.measurement;
      if (entry?.type !== 'vital' || !measurement) return false;
      const metric = owns(VITAL_METRICS, measurement.metric) && VITAL_METRICS[measurement.metric];
      const timestamp = Date.parse(entry.occurredAt);
      if (
        !metric ||
        measurement.unit !== metric.unit ||
        !finitePositive(measurement.value) ||
        (measurement.context != null && typeof measurement.context !== 'string') ||
        !Number.isFinite(timestamp) ||
        timestamp > now
      ) {
        return false;
      }
      if (measurement.metric === 'blood_pressure') {
        return (
          finitePositive(measurement.secondaryValue) &&
          measurement.value > measurement.secondaryValue
        );
      }
      return measurement.metric !== 'glucose' || owns(CONTEXTS, measurement.context);
    })
    .map((entry) => ({ ...entry, timestamp: Date.parse(entry.occurredAt) }))
    .sort((a, b) => a.timestamp - b.timestamp);

const segmentPoints = (points, maximumGap) => {
  const segments = [];
  points.forEach((point, index) => {
    if (!index || point.timestamp - points[index - 1].timestamp > maximumGap) segments.push([]);
    segments[segments.length - 1].push(point);
  });
  return segments;
};

export const buildVitalsSeries = (entries, metric, range, context = 'all', now = Date.now()) => {
  const cutoff = range === 'all' ? -Infinity : now - Number(range) * DAY;
  const readings = normalizeVitalEntries(entries, now).filter(
    (entry) =>
      entry.measurement.metric === metric &&
      entry.timestamp >= cutoff &&
      (metric !== 'glucose' || context === 'all' || entry.measurement.context === context)
  );
  const firstTime = readings[0]?.timestamp;
  const lastTime = readings[readings.length - 1]?.timestamp;
  const start =
    range === 'all' ? (firstTime ?? now) - (firstTime === lastTime ? DAY / 2 : 0) : cutoff;
  const end = range === 'all' ? (lastTime ?? now) + (firstTime === lastTime ? DAY / 2 : 0) : now;
  const span = Math.max(end - start, 1);
  const maximumGap = Math.max(DAY, span / 6);
  const x = (timestamp) => PLOT.left + ((timestamp - start) / span) * (PLOT.right - PLOT.left);
  let definitions;
  if (metric === 'blood_pressure') {
    definitions = [
      { key: 'systolic', label: 'Systolic', field: 'value', color: '#087e83', dash: undefined },
      {
        key: 'diastolic',
        label: 'Diastolic',
        field: 'secondaryValue',
        color: '#4864a0',
        dash: '6 4'
      }
    ];
  } else if (metric === 'glucose') {
    definitions = Object.entries(CONTEXTS)
      .filter(([key]) => context === 'all' || context === key)
      .map(([key, label], index) => ({
        key,
        label,
        field: 'value',
        color: ['#087e83', '#9b5e15', '#7557a2'][Object.keys(CONTEXTS).indexOf(key)],
        dash: index ? (index === 1 ? '6 4' : '2 4') : undefined,
        context: key
      }));
  } else {
    definitions = [
      { key: metric, label: VITAL_METRICS[metric].label, field: 'value', color: '#087e83' }
    ];
  }
  const values = readings.flatMap((entry) =>
    metric === 'blood_pressure'
      ? [entry.measurement.value, entry.measurement.secondaryValue]
      : [entry.measurement.value]
  );
  const min = values.length ? Math.min(...values) : 0;
  const max = values.length ? Math.max(...values) : 1;
  const padding = Math.max((max - min) * 0.15, max * 0.025, 0.5);
  const yMin = Math.max(0, min - padding);
  const yMax = Number.isFinite(max + padding) ? max + padding : max;
  const y = (value) => PLOT.bottom - ((value - yMin) / (yMax - yMin)) * (PLOT.bottom - PLOT.top);
  const series = definitions.map((definition) => {
    const points = readings
      .filter((entry) => !definition.context || entry.measurement.context === definition.context)
      .map((entry) => ({
        entry,
        timestamp: entry.timestamp,
        value: entry.measurement[definition.field],
        x: x(entry.timestamp),
        y: y(entry.measurement[definition.field])
      }));
    return { ...definition, points, segments: segmentPoints(points, maximumGap) };
  });
  return { readings, series, start, end, x, y, yMin, yMax, maximumGap };
};

const defaults = { metric: 'blood_pressure', range: '30', context: 'all' };
const restoreSelection = (saved) => ({
  metric: owns(VITAL_METRICS, saved?.metric) ? saved.metric : defaults.metric,
  range: ['7', '30', '90', 'all'].includes(saved?.range) ? saved.range : defaults.range,
  context:
    saved?.context === 'all' || owns(CONTEXTS, saved?.context) ? saved.context : defaults.context
});
const readingValue = (entry) => {
  const measurement = entry.measurement;
  return `${measurement.value}${measurement.metric === 'blood_pressure' ? ` / ${measurement.secondaryValue}` : ''} ${measurement.unit}`;
};

const VitalsChart = ({ entries = [], events = [], storageKey }) => {
  const [selection, setSelection] = useSessionState(storageKey, defaults, {
    restore: restoreSelection
  });
  const { metric, range, context } = selection;
  const setFilter = (key, value) => setSelection((previous) => ({ ...previous, [key]: value }));
  const now = Date.now();
  const chart = buildVitalsSeries(entries, metric, range, context, now);
  const latest = chart.readings[chart.readings.length - 1];
  const eventMarkers = (Array.isArray(events) ? events : [])
    .filter((event) => ['event', 'prescription'].includes(event?.type))
    .map((event) => ({ ...event, timestamp: Date.parse(event.occurredAt) }))
    .filter(
      (event) =>
        Number.isFinite(event.timestamp) &&
        event.timestamp <= now &&
        event.timestamp >= chart.start &&
        event.timestamp <= chart.end
    )
    .sort((a, b) => a.timestamp - b.timestamp);
  const hasGaps = chart.series.some((series) => series.segments.length > 1);
  return (
    <section className="vitals-card" aria-label="Vitals over time">
      <div className="vitals-heading">
        <div>
          <span className="vitals-eyebrow">THE LONGER VIEW</span>
          <h2>Vitals over time</h2>
          <p>Readings and the events around them.</p>
        </div>
        <span className="vitals-count">
          {chart.readings.length} {chart.readings.length === 1 ? 'reading' : 'readings'}
        </span>
      </div>
      <div className="vitals-controls">
        <label>
          Measurement
          <select
            aria-label="Vital measurement"
            value={metric}
            onChange={(event) => setFilter('metric', event.target.value)}
          >
            {Object.entries(VITAL_METRICS).map(([key, item]) => (
              <option key={key} value={key}>
                {item.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          Time period
          <select
            aria-label="Vitals time period"
            value={range}
            onChange={(event) => setFilter('range', event.target.value)}
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="all">All readings</option>
          </select>
        </label>
        {metric === 'glucose' && (
          <label>
            Reading context
            <select
              aria-label="Glucose context"
              value={context}
              onChange={(event) => setFilter('context', event.target.value)}
            >
              <option value="all">All contexts (separate lines)</option>
              {Object.entries(CONTEXTS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {!latest ? (
        <div className="vitals-empty" role="status">
          <span aria-hidden="true">—</span>
          <h3>No readings in this view</h3>
          <p>Recorded measurements will appear here. Try another measurement or time period.</p>
        </div>
      ) : (
        <>
          <div className="vitals-latest" aria-live="polite">
            <div>
              <span className="vitals-small-label">
                Latest {VITAL_METRICS[metric].label.toLowerCase()}
              </span>
              <strong>{readingValue(latest)}</strong>
            </div>
            <div className="vitals-latest-meta">
              <time dateTime={latest.occurredAt}>{dateTimeLabel(latest.timestamp)}</time>
              <span>
                {source(latest)}
                {latest.measurement.context ? ` · ${contextLabel(latest.measurement.context)}` : ''}
              </span>
            </div>
          </div>
          <div className="vitals-legend" aria-label="Chart series">
            {chart.series
              .filter((series) => series.points.length)
              .map((series) => (
                <span key={series.key}>
                  <svg width="24" height="12" aria-hidden="true">
                    <line
                      x1="0"
                      x2="24"
                      y1="6"
                      y2="6"
                      stroke={series.color}
                      strokeWidth="3"
                      strokeDasharray={series.dash}
                    />
                  </svg>
                  {series.label}
                </span>
              ))}
          </div>
          <div
            className="vitals-plot-scroll"
            tabIndex="0"
            role="region"
            aria-label="Vitals chart; scroll horizontally on small screens"
          >
            <svg
              className="vitals-plot"
              viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
              role="img"
              aria-label={`${VITAL_METRICS[metric].label} over time: ${chart.readings.length} recorded readings. Exact values, dates and sources are in the readings table below.`}
            >
              <desc>
                Horizontal positions represent elapsed time. Lines join recorded readings only and
                break across long gaps. Glucose contexts have separate series. Event markers
                indicate timing, not cause.
              </desc>
              <text x={PLOT.left} y="13" className="vitals-axis-label">
                {VITAL_METRICS[metric].unit}
              </text>
              {[0, 1, 2, 3, 4].map((index) => {
                const value = chart.yMin + (chart.yMax - chart.yMin) * (index / 4);
                return (
                  <g key={index}>
                    <line
                      className="vitals-grid"
                      x1={PLOT.left}
                      x2={PLOT.right}
                      y1={chart.y(value)}
                      y2={chart.y(value)}
                    />
                    <text
                      className="vitals-axis-label"
                      x={PLOT.left - 10}
                      y={chart.y(value) + 4}
                      textAnchor="end"
                    >
                      {Number(value.toFixed(1))}
                    </text>
                  </g>
                );
              })}
              {[0, 1, 2, 3, 4].map((index) => {
                const timestamp = chart.start + ((chart.end - chart.start) * index) / 4;
                return (
                  <text
                    key={index}
                    className="vitals-axis-label"
                    x={chart.x(timestamp)}
                    y="245"
                    textAnchor={index === 0 ? 'start' : index === 4 ? 'end' : 'middle'}
                  >
                    {dateLabel(timestamp)}
                  </text>
                );
              })}
              {eventMarkers.map((event, index) => (
                <g key={event.id || `${event.timestamp}-${index}`}>
                  <title>
                    {index + 1}. {event.title || 'Clinical event'} —{' '}
                    {dateTimeLabel(event.timestamp)}
                  </title>
                  <line
                    className="vitals-event-line"
                    x1={chart.x(event.timestamp)}
                    x2={chart.x(event.timestamp)}
                    y1={PLOT.top}
                    y2={PLOT.bottom + 14}
                  />
                  <circle
                    className="vitals-event-dot"
                    cx={chart.x(event.timestamp)}
                    cy={PLOT.bottom + 14}
                    r="8"
                  />
                  <text
                    x={chart.x(event.timestamp)}
                    y={PLOT.bottom + 17}
                    textAnchor="middle"
                    className="vitals-event-number"
                  >
                    {index + 1}
                  </text>
                </g>
              ))}
              {chart.series.map((series) => (
                <g key={series.key} data-series={series.key}>
                  {series.segments
                    .filter((segment) => segment.length > 1)
                    .map((segment, index) => (
                      <polyline
                        key={index}
                        points={segment.map((point) => `${point.x},${point.y}`).join(' ')}
                        fill="none"
                        stroke={series.color}
                        strokeWidth="2.5"
                        strokeDasharray={series.dash}
                      />
                    ))}
                  {series.points.map((point, index) => (
                    <circle
                      key={point.entry.id || index}
                      cx={point.x}
                      cy={point.y}
                      r="4"
                      stroke="#fff"
                      strokeWidth="1.5"
                      fill={series.color}
                    >
                      <title>
                        {series.label}: {point.value} {VITAL_METRICS[metric].unit} ·{' '}
                        {dateTimeLabel(point.timestamp)} · {source(point.entry)}
                      </title>
                    </circle>
                  ))}
                </g>
              ))}
            </svg>
          </div>
          <p className="vitals-chart-note">
            {chart.readings.length === 1
              ? 'One reading recorded; more readings are needed to show change over time.'
              : 'Points show recorded values; spacing reflects elapsed time.'}
            {hasGaps &&
              ' Lines are broken where readings are more than ' +
                Number((chart.maximumGap / DAY).toFixed(1)) +
                ' days apart.'}
            {metric === 'glucose' && ' Glucose contexts are shown separately.'}
          </p>
          {eventMarkers.length > 0 && (
            <div className="vitals-events">
              <h3>Events in this period</h3>
              <ol>
                {eventMarkers.map((event, index) => (
                  <li key={event.id || index}>
                    <strong>{event.title || 'Clinical event'}</strong>
                    <time dateTime={event.occurredAt}>{dateTimeLabel(event.timestamp)}</time>
                  </li>
                ))}
              </ol>
              <p>
                Events provide timing context. Their alignment with a reading does not establish a
                cause.
              </p>
            </div>
          )}
          <details className="vitals-readings">
            <summary>View recorded readings ({chart.readings.length})</summary>
            <div
              className="vitals-table-scroll"
              tabIndex="0"
              role="region"
              aria-label="Recorded readings table"
            >
              <table>
                <caption>
                  {VITAL_METRICS[metric].label}: exact readings, in chronological order. Dates use
                  your local time zone.
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Date and time</th>
                    <th scope="col">Reading</th>
                    <th scope="col">Context</th>
                    <th scope="col">Recorded by</th>
                  </tr>
                </thead>
                <tbody>
                  {chart.readings.map((entry, index) => (
                    <tr key={entry.id || index}>
                      <td>
                        <time dateTime={entry.occurredAt}>{dateTimeLabel(entry.timestamp)}</time>
                      </td>
                      <td>{readingValue(entry)}</td>
                      <td>{contextLabel(entry.measurement.context) || 'Not specified'}</td>
                      <td>{source(entry)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </>
      )}
    </section>
  );
};

export default VitalsChart;
